<template>
  <div class="order-form">
    <div class="container">
      <vue-sticky-sidebar containerSelector=".order-form" :class="[isComplete, 'sidebar']">
        <cart-summary
            :summary-list="summaryList"
            :currency-info="currencyInfo"
            :route-query="routeQuery"
            :payment-periods="billingcycles"
            :pay-type="initialData?.product?.paytype"
            :domain-type="domainType"
            @freeTrial="getFreeTrial($event)"
            @freeDomain="getFreeDomain($event)"
            @currentPromo="getCurrentPromo($event)"
            :monthly-discount40="monthlyDiscount40"
        />

        <feedback
            :initial-data="initialData"
            :flow="flow"
            :domain-type="domainType"
        />
      </vue-sticky-sidebar>

      <section class="content settings">
        <ol :class="[isTrialFlow ? 'steppers' : '', 'breadcrumbs']">
          <li
              v-for="(item, key) in isTrialFlow ? $t(`breadcrumbs.test`) : $t(`breadcrumbs.default`)"
              :value="item"
              :key="key"
              @click="isTrialFlow ? goToStep(key) : $event.preventDefault()"
              :class="isTrialFlow ? getBreadcrumbsClass(key, $t(`breadcrumbs.test`)) : ''"
          >{{item}}</li>
        </ol>

        <div v-show="isVisibleStep(0)">
          <info-data
              v-if="testSsl || autoSsl"
              :test-ssl="testSsl"
              :auto-ssl="autoSsl"
          />

          <payment-periods
              v-show="isVisiblePaymentPeriods"
              @paymentPeriod="(!isVisiblePaymentPeriods && isVisibleDomainName) || getPaymentPeriod($event)"
              :payment-periods="billingcycles"
              :free-domain-info="freeDomainInfo"
              :currency-info="currencyInfo"
              :domain-type="domainType"
              :free-trial="freeTrial"
              :route-query="routeQuery"
              :monthly-discount40="monthlyDiscount40"
              :promo="cartContext.promo"
              :period-for-free-ssl="initialData?.period_for_free_ssl"
          />

          <domain-name
              v-if="isVisibleDomainName"
              ref="domainName"
              @domainName="getDomainName($event)"
              :currency-info="currencyInfo"
              :initial-domain-opt="domainOpt"
              :free-domain-info="freeDomainInfo"
              :payment-period="paymentPeriod"
              :domain-type="domainType"
              :route-query="routeQuery"
              :flow="flow"
              @paymentPeriod="!isVisibleDomainName || getPaymentPeriod($event)"
              @dataPeriod="getDataPeriod($event)"
              @loading="getLoading($event, 'domainName')"
          />

          <template v-if="isValidDomain">
            <extra-options
                v-show="configOptions?.length > 0 && flow !== $options.FLOW.DOMAIN"
                :currency-info="currencyInfo"
                :config-opt="configOptions"
                :payment-period="paymentPeriod"
                :domain-type="domainType"
                data-attr="extra-options"
                :is-wordpress-v3="isWordpressV3"
            />

            <additional-options
                :add-opt="addOptions"
                v-if="addOptions.length > 0"
            />
          </template>

          <upsell v-if="isVisibleUpsell()"
                  :flow="upsellFlow"
                  :init-upsell-data="upSells"
                  :currency-info="currencyInfo"
                  @upsell="updateSummaryUpsells($event)"
                  :startChat="addLiveChatScript"
                  :is-free-ssl-period="isFreeSslPeriod"
          />

        </div>

        <authorization
            v-show="isVisibleStep(1) && isValidDomain"
            :isAuth="isAuth"
            :flow="flow"
            ref="authorization"
            :is-virtual-hosting-v2="isVirtualHostingV2"
        ></authorization>

        <div
            class="block card-verification"
            v-if="isVisibleStep(1) && isVirtualHostingV2"
        >
          <h2>{{ $t('auth.cardVerification') }}</h2>
          <p>{{ $t('auth.cardVerificationInfo') }}</p>
        </div>

      </section>

      <section class="content completion" v-if="isTrialFlow ? true : isValidDomain && isAuth">

        <payment-methods
            v-if="isVisiblePaymentMethods"
            :currency-info="currencyInfo"
            :flow="flow"
            :free-domain="freeDomain"
            :domain-type="domainType"
            @paymentMethod="getPaymentMethod($event)"
            @loading="getLoading($event, 'paymentMethods')"
            :is-virtual-hosting-v2="isVirtualHostingV2"
        />
        <template v-if="!loading">
          <div v-if="isTrialFlow && isVisibleStep(0)" class="submit-block">
            <template v-if="isValidDomain">
              <div class="unit">
                <v-btn
                    :ripple="false"
                    @click="goToStep(1)"
                    color="primary"
                    data-attr="go-to-second-step"
                >{{ $t('actions.continue') }}</v-btn>
              </div>
              <p v-html="showTrialBenefit ? $t('trialBenefit') : $t('trialBenefitPercent')"></p>
            </template>
          </div>

          <div
              v-else
              class="submit-block">
            <div class="unit">
              <v-btn
                  :ripple="false"
                  @click="cartOrder()"
                  color="secondary"
                  data-attr="of-submit"
              >{{ domainType === $options.DOMAIN_TYPE.TEST_SSL ? $t('actions.freeTest') : $t('actions.checkout')}}</v-btn>
            </div>
            <p
                v-html="$t('agreementsBlock',
              { serviceTermsLink: $options.URLS.SERVICE_TERMS[this.lang], serviceTermsAttr: 'service-terms',
              publicOfferLink: publicOffer, publicOfferAttr: 'public-offer',
              privacyPolicyLink: $options.URLS.PRIVACY_POLICY[this.lang], privacyPolicyAttr: 'privacyPolicy'})"></p>
            <ul class="cards">
              <li v-for="card in $options.CARDS.LIST" :key="card.name">
                <img :src="require(`@/assets/images/${card.image}.svg`)" :alt="card.name">
              </li>
            </ul>
          </div>
        </template>

      </section>
    </div>

    <popup-loader v-if="loadingCartOrder" :show-popup="loadingCartOrder" :title="$t('loaderText')"/>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { SETTINGS_GETTERS, SETTING_ACTIONS, SETTINGS_MUTATIONS } from "@/store/modules/settings";
  import { CART_GETTERS, CART_ACTIONS, CART_MUTATIONS } from "@/store/modules/cart-context";
  import { AUTH_GETTERS, AUTH_ACTIONS } from "@/store/modules/auth";
  import { CARDS, CURRENCY, DOMAIN_OPT, DOMAIN_TYPE, FLOW, LANG, DISK_TYPE, PROMO, URLS, UPSELL_FLOW } from '@/constants';
  import { getInitData, getInitDataProduct } from "@/store/api/settings";
  import { cartOrder } from "@/store/api/auth";
  import { zopimLivechat } from '@/modules/zopimLivechat';
  import { nameTranslate } from '@/modules/nameTranslate';
  import PopupLoader from '@/shared/components/Loaders/PopupLoader.vue';
  import * as Sentry from "@sentry/vue";
  import Upsell from "@/pages/orderForm/components/Upsell/Upsell.vue";
  import InfoData from "./components/InfoData.vue";
  import PaymentPeriods from "./components/PaymentPeriods.vue";
  import CartSummary from "./components/CartSummary.vue";
  import Feedback from "./components/Feedback.vue";
  import DomainName from "./components/DomainName.vue";
  import ExtraOptions from "./components/ExtraOptions.vue";
  import AdditionalOptions from "./components/AdditionalOptions.vue";
  import Authorization from "./components/Authorization/Auth.vue";
  import PaymentMethods from "./components/PaymentMethods.vue";

  export default {
    name: "OrderForm",
    CARDS,
    DOMAIN_OPT,
    DOMAIN_TYPE,
    URLS,
    FLOW,
    components: {
      Upsell,
      InfoData,
      PaymentPeriods,
      CartSummary,
      Feedback,
      DomainName,
      ExtraOptions,
      AdditionalOptions,
      PaymentMethods,
      Authorization,
      PopupLoader,
    },
    data() {
      return {
        loading: false,
        loadingCartOrder: false,
        routeQuery: {},
        flow: FLOW.DEFAULT,
        domainType: '',
        freeTrial: false,
        freeDomain: false,
        currentPromo: '',
        initialData: undefined,
        billingcycles: undefined,
        freeDomainInfo: undefined,
        currencyInfo: undefined,
        domainOpt: '',
        summaryList: [],
        paymentPeriod: {},
        configOptions: [],
        addOptions: [],
        testSsl: undefined,
        autoSsl: undefined,
        payType: undefined,

        currentStep: 0,
        publicOffer: '',
        geoIp: undefined,

        paymentMethod: undefined,
        gtmTrackEvent: false,

        loadingDomainName: undefined,
        loadingPaymentMethods: undefined,

        upSells: {},
        allowedSslHostingTypes: [DOMAIN_TYPE.SHARED, DOMAIN_TYPE.VIP, DOMAIN_TYPE.SITE_BUILDER, DOMAIN_TYPE.ASTRA, DOMAIN_TYPE.EXTENDIFY],
        isFreeSslPeriod: false,
        orderData: {}
      }
    },
    mounted() {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({'pageCategory' : 'Order form'});
      this.getInitialData();
      this.addLiveChatScript();
    },
    computed: {
      ...mapGetters('settings', {
        lang: SETTINGS_GETTERS.LANG,
        currency: SETTINGS_GETTERS.CURRENCY,
        currencySuffix: SETTINGS_GETTERS.CURRENCY_SUFFIX,
        currencyPrefix: SETTINGS_GETTERS.CURRENCY_PREFIX,
        authorization: SETTINGS_GETTERS.AUTH,
        trialAuth: SETTINGS_GETTERS.TRIAL_AUTH,
        totalPrice: SETTINGS_GETTERS.TOTAL_PRICE,
        affiliate: SETTINGS_GETTERS.AFFILIATE,
      }),
      ...mapGetters('cartContext', {
        cartContext: CART_GETTERS.CART_CONTEXT,
      }),
      ...mapGetters('auth', {
        verifyHash: AUTH_GETTERS.VERIFY_HASH,
        verification: AUTH_GETTERS.VERIFICATION,
      }),
      isTrialFlow() {
        return this.flow === FLOW.TRIAL;
      },
      isAuth() {
        return this.authorization && this.authorization.auth && !!Object.keys(this.authorization.auth).length || false;
      },
      isValidDomain() {
        let validate = false;
        if (this.summaryList.length > 0) {
          this.summaryList.forEach(item => {
            if (item.key === 'domain-name' && item.isValidDomain) {
              validate = true;
            }
          });
        }
        if(this.domainOpt && this.domainOpt !== DOMAIN_OPT.REQUIRED) { validate = true }
        return validate;
      },
      isComplete() {
        return this.isTrialFlow && this.currentStep === 1 || !this.isTrialFlow && this.isValidDomain && this.isAuth ? 'is-complete': '';
      },
      isVisiblePaymentPeriods() {
        return this.flow !== FLOW.DOMAIN && this.payType !== DOMAIN_TYPE.FREE && this.payType !== DOMAIN_TYPE.ONETIME;
      },
      isVisibleDomainName() {
        return !this.routeQuery.pid || this.domainOpt === DOMAIN_OPT.REQUIRED;
      },
      isVisiblePaymentMethods() {
        return (this.isVirtualHostingV2 && this.isVisibleStep(1)) ||
          (this.domainType !== DOMAIN_TYPE.TEST_SSL &&
            (!this.isTrialFlow || this.isVisibleStep(2)) &&
            Number(this.totalPrice) > 0);
      },
      showTrialBenefit() {
        return this.cartContext.promo.toLowerCase().includes(PROMO.FREE_TRIAL) || this.cartContext.promo.toLowerCase().includes(PROMO.GIFT)
      },
      monthlyDiscount40() {
        return this.initialData?.forty_percent_promo?.includes(this.currentPromo?.toUpperCase());
      },
      upsellFlow() {
        let flow = '';

        if (this.flow === FLOW.DOMAIN) {
          flow = UPSELL_FLOW.SSL_DOMAIN;
        } else if(this.allowedSslHostingTypes.includes(this.domainType)) {
          flow = UPSELL_FLOW.SSL_HOSTING
        }
        return flow;
      },
      isVirtualHostingV2() {
        const hasNoSavedCard = !this.isAuth || this.authorization?.auth?.has_saved_card === false;
        return this.flow === FLOW.TRIAL && (document.referrer || '').includes('/virtual-hosting-v2') && hasNoSavedCard && Number(this.totalPrice) === 0
      },
      isWordpressV3() {
        return (document.referrer || '').includes('/cms-hosting/wordpress-v3') && this.initialData?.product?.nvm_disk_type === true
      }
    },
    methods: {
      nameTranslate,
      ...mapActions('settings', {
        setCurrencySuffix: SETTING_ACTIONS.SET_CURRENCY_SUFFIX,
        setCurrencyPrefix: SETTING_ACTIONS.SET_CURRENCY_PREFIX,
        setTrial: SETTING_ACTIONS.SET_TRIAL,
        setAuth: SETTING_ACTIONS.SET_AUTH,
        setTotalPrice: SETTING_ACTIONS.SET_TOTAL_PRICE,
        setAffiliate: SETTING_ACTIONS.SET_AFFILIATE,
      }),
      ...mapActions('cartContext', {
        setСartContext: CART_ACTIONS.SET_CART_CONTEXT,
      }),
      ...mapActions('auth', {
        setVerifyHash: AUTH_ACTIONS.SET_VERIFY_HASH,
        setVerification: AUTH_ACTIONS.SET_VERIFICATION,
      }),
      zopimLivechat,
      addLiveChatScript() {
        if (window.zE) { return Promise.resolve(); }

        return new Promise(() => {
          const zopomLivechatScript = document.createElement('script');
          zopomLivechatScript.type = 'text/javascript';
          zopomLivechatScript.innerHTML = this.zopimLivechat(this.lang.toLowerCase());

          const zeTag = document.createElement('script');
          zeTag.id = 'ze-snippet';
          zeTag.src = 'https://static.zdassets.com/ekr/snippet.js?key=02372ab6-38d7-4763-ab4f-8170f9035e07';

          document.head.appendChild(zeTag);
          document.head.appendChild(zopomLivechatScript);
        });
      },
      getBreadcrumbsClass(key, items) {
        const itemClass = [];
        if(this.currentStep === key) {
          itemClass.push('active');
        } else if(this.currentStep > key) {
          itemClass.push('complete');
        };
        if(this.currentStep !== key && items.length - 1 !== key) {
          if(this.currentStep === 0 && this.isValidDomain || this.currentStep > key) {
            itemClass.push('hover');
          }
        }
        return itemClass;
      },
      getLoading(val, key) {
        if(key === 'domainName') {
          this.loadingDomainName = val
        } else if(key === 'paymentMethods') {
          this.loadingPaymentMethods = val
        }
        this.loading = this.loadingDomainName || this.loadingPaymentMethods;
      },
      isValidPhone() {
        return this.$refs.phone.validate();
      },
      goToStep(key) {
        if(key === 0) {
          this.currentStep = key;
          this.$vuetify.goTo(0);
        } else if(key === 1) {
          this.loading = true;
          this.$nextTick(() => {
            if (this.isValidDomain && this.$refs.domainName.validateCustomFields()) {
              this.currentStep = key;
              this.$vuetify.goTo(0);
            }
            this.loading = false;
          })
        }
      },
      isVisibleStep(key) {
        let visible = true;
        if(this.isTrialFlow) {
          visible = this.currentStep === key;
        }
        return visible;
      },
      async getInitialData() {
        this.publicOffer = URLS.PUBLIC_OFFER[this.lang];

        this.routeQuery = this.$route.query;
        if(!this.routeQuery.pid) {
          if(!this.routeQuery.domain) {
            this.goToHome();
          } else {
            this.flow = FLOW.DOMAIN
          }
        }
        if(this.routeQuery.pid && this.routeQuery.promo && this.routeQuery.promo.toLowerCase().includes(PROMO.FREE_TRIAL)) { this.flow = FLOW.TRIAL };

        const pid = this.routeQuery.pid ? Number(this.routeQuery.pid) : 405;
        const hash = this.routeQuery?.hash;
        this.$store.commit(`cartContext/${CART_MUTATIONS.SET_CART_CONTEXT}`, { pid });

        this.loading = true;
        if (!this.lang) { this.lang = await this.setLang(); }
        if (!this.currency) { this.currency = this.setCurrency(); }
        if (!this.currencySuffix) { this.currencySuffix = this.setCurrencySuffix(); }
        if (!this.authorization) { this.authorization = this.setAuth(); }
        if (!this.cartContext) { this.cartContext = this.setСartContext(); }
        if (!this.totalPrice) { this.setTotalPrice(); }
        if (!this.affiliate) { this.setAffiliate(); }

        const initDataOptions = {
          upsale_ssl_on_domain: this.upsellFlow === UPSELL_FLOW.SSL_DOMAIN
        };

        try {
          this.initialData = this.routeQuery.pid ? await getInitDataProduct(pid, hash) : await getInitData(hash, initDataOptions);
          let setAuth = {};
          if(this.authorization.cookie && this.initialData.client) {
            setAuth = {
              cookie: this.authorization.cookie,
              auth: this.initialData.client
            }
          }
          this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_AUTH}`, setAuth);
        } catch (error) {
          Sentry.captureException(error);
          this.$notify('error', `Error ${error.status}`, `${error.statusText}`, { copy: true });
          this.goToHome();
        }  finally {
          this.loading = false;
          this.computedProductDetails(hash);
          this.addInitUpsells();
          this.gtmEvent();
        }
      },

      validateOrder() {
        let validate = false;
        if(this.isVirtualHostingV2 && this.isAuth) {
          validate = true
        } else if(this.flow === FLOW.TRIAL) {
          validate = this.trialAuth || this.$refs.authorization.submit()
        } else {
          const validateDomainName = this.isVisibleDomainName ? this.$refs.domainName.validateCustomFields() : true;
          validate = validateDomainName && this.isAuth;
        }
        return validate;
      },

      async cartOrder() {
        if(!this.validateOrder()) return
        this.loadingCartOrder = true;

        try {
          const paymentData = {
            paymentmethod: this.paymentMethod?.name || "banktransfer",
            apply_balance: this.paymentMethod?.amount,
            verification: this.isTrialFlow ? this.verification : undefined,
            affid: this.affiliate,
            ssl_auto_setup_domain: this.initialData.ssl_auto_setup?.domain,
            save_card_on_trial: this.isVirtualHostingV2 ? true : undefined
          };

          const { currency, language, ...orderData2 } = this.cartContext;
          const orderData = orderData2
          const configOpts = orderData.products[0].config_opts;
          const nvmDiskType = configOpts?.diskType === 2 ? true : undefined;
          delete configOpts.diskType;

          const result = await cartOrder(this.authorization.auth, this.authorization.cookie, paymentData, this.verifyHash, orderData, nvmDiskType);
          if(result.status === "Unpaid" && result.form) {
            const parser = new DOMParser();
            const html = parser.parseFromString(result.form, 'text/html');
            const form = html.getElementsByTagName('form')[0];
            document.body.append(form);
            form.submit();
          } else {
            if(result.invoice_link) {
              if ([
                "fondynewcard",
                "tco",
                "monobank_newcard_ua",
                "monobank_newcard_global",
                "monobank_newcard_company",
                "monobank_newcard_pay_individual",
                "monobank_newcard_pay_legal"
              ].includes(this.paymentMethod?.name)) {
                // eslint-disable-next-line no-restricted-globals
                history.pushState(null, null, `/clients/viewinvoice.php?id=${result.invoiceid}&backinvoice=true`);
              }
              window.location.href = result.invoice_link
            };
          }
        } catch (error) {
          Sentry.captureException(error);
          this.$notify('error', `Error ${error.status}`, error.statusText, { copy: true });
          setTimeout(() => { this.loadingCartOrder = false; }, 1000)
        }  finally {
          this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_TRIAL_AUTH}`, false);
        }
      },

      computedProductDetails(hash) {
        if(hash && (this.initialData.ssl_auto_setup?.success !== true || !this.initialData.ssl_auto_setup?.domain)) {
          window.location.href = URLS.SSL[this.lang];
        }
        if(this.initialData && this.initialData.product) {
          this.billingcycles = this.initialData.product.billingcycles.filter(item => item.currency_code === this.currency && item.available === true);
          this.freeDomainInfo = {
            freeDomainTlds: this.initialData.product.freedomaintlds,
            freeDomain: this.initialData.product.freedomain,
            freedomainpaymentterms: this.initialData.product.freedomainpaymentterms
          };
          this.domainOpt = this.initialData.product.domainopt || DOMAIN_OPT.NONE;
          this.addOptions = this.initialData.product.customfields;
          this.domainType = this.initialData.product.type;
          this.testSsl = this.initialData.product.testssl || undefined;
          this.autoSsl = this.initialData.ssl_auto_setup?.domain || undefined;
          this.payType = this.initialData.product.paytype;
        }
        this.currencyInfo = this.initialData?.currencies.find((item) => {
          item.lang = this.lang;
          return item.code === this.currency;
        }) || {};
        this.setCurrencySuffix();
        this.currencyInfo.suffix = this.currencySuffix;
        this.$store.dispatch(`settings/${SETTING_ACTIONS.SET_CURRENCY_PREFIX}`, this.currencyInfo.prefix);
        if(this.flow !== FLOW.DOMAIN) { this.configOptions = this.initialData?.product?.config_opts; }

        if(this.isWordpressV3) {
          const index = this.configOptions.findIndex(option => option.id === 33);
          if (index !== -1) {
            this.configOptions.splice(index + 1, 0, DISK_TYPE.NVM);
          }
        }

        if(this.domainType === DOMAIN_TYPE.CLOUD) { this.presetPaymentPeriod({key: 'payment-period'}) }

        this.addInitUpsells();
      },

      addInitUpsells() {
        const fields = [
          { source: 'up_sale_ssl', target: 'ssl' }
        ];
        fields.forEach(field => {
          if (this.initialData?.[field.source] && Array.isArray(this.initialData[field.source]) && this.initialData[field.source].length > 0) {
            this.upSells[field.target] = [];
            this.initialData[field.source].forEach(item => {
              this.upSells[field.target].push(item);
            });
          }
        });
      },

      presetPaymentPeriod(period) {
        if (!period.key) {
          return;
        }
        const index = this.summaryList.findIndex((el) => el.key === 'payment-period');
        if(index === -1) {
          this.summaryList.unshift(period);
        } else {
          this.$set(this.summaryList, index, period);
        }
      },

      getPaymentPeriod(period) {
        this.paymentPeriod = period;
        this.paymentPeriod.name = this.initialData && this.initialData.product ? this.nameTranslate(this.initialData.product) : period.name;
        this.paymentPeriod.title = this.initialData && this.initialData.product ? this.nameTranslate(this.initialData.product.group) : period.title;
        this.$store.commit(`cartContext/${CART_MUTATIONS.SET_CART_CONTEXT}`, { billingcycle: this.paymentPeriod.period });

        this.presetPaymentPeriod(period);

        if(this.flow === FLOW.DOMAIN) { this.$set(this.routeQuery, 'promo', period.promo) };
      },

      getDataPeriod(data) {
        this.configOptions = data.options;
        this.billingcycles = data.cycle;
      },

      getDomainName(domain) {
        const index = this.summaryList.findIndex((el) => el.key === 'domain-name');
        if(index === -1) {
          this.summaryList.push(domain);
        } else {
          this.$set(this.summaryList, index, domain);
        }
      },

      getFreeTrial(flag) {
        this.freeTrial = flag;
      },

      getFreeDomain(flag) {
        this.freeDomain = flag;
      },

      getCurrentPromo(promo) {
        this.currentPromo = promo;
      },

      getPaymentMethod(method) {
        this.paymentMethod = method;
        const isVisiblePaymentMethod = this.isTrialFlow ? true : this.isValidDomain && this.isAuth;
        if(isVisiblePaymentMethod && method) {
          let langPreffix = '';
          if(this.lang === 'UA') { langPreffix = '/ukr'}
          else if(this.lang === 'EN') { langPreffix = '/eng'}
          this.publicOffer = `https://hostiq.ua${langPreffix}${method.offer_url}`
        }
      },

      goToHome() {
        setTimeout(() => { window.location.href = URLS.HOME[this.lang] }, 2000)
      },
      gtmEvent() {
        const dynx = {
          dynx_itemid: this.routeQuery.pid,
          dynx_pagetype: 'conversionintent',
          dynx_totalvalue: 0
        };

        if(this.flow === FLOW.DOMAIN && this.isValidDomain && !this.gtmTrackEvent) {
          this.gtmTrackEvent = true;

          const domain = Object.values(this.summaryList).find((el) => el.key === 'domain-name');
          dynx.dynx_itemid = domain.tld;
          dynx.dynx_totalvalue = domain.price;

          window.dataLayer.push(dynx);
        } else if(this.billingcycles){
          this.gtmTrackEvent = true;

          dynx.dynx_totalvalue = this.billingcycles[0]?.price || '';
          Object.values(this.billingcycles).forEach(item => {
            if(item.month === 12) { dynx.dynx_totalvalue = (Math.round((Number(item.price) / 12) * 100) / 100) }
          });
          window.dataLayer.push(dynx);
        }
      },
      updateSummaryUpsells(upsells) {
        const upsellKeys = upsells.map((upsell) => upsell.key);

        this.summaryList = this.summaryList.filter((el) => {
          if (el.key.startsWith('upsell-')) {
            return upsellKeys.includes(el.key);
          }
          return true;
        });

        upsells.forEach((upsell) => {
          const index = this.summaryList.findIndex((el) => el.key === upsell.key);
          if (index === -1) {
            this.summaryList.push(upsell);
          } else {
            this.$set(this.summaryList, index, upsell);
          }
        });
      },
      isVisibleUpsell() {
        let visible = this.upsellFlow && Object.keys(this.upSells).length > 0;

        if(this.flow !== FLOW.DOMAIN) {
          visible = this.allowedSslHostingTypes.includes(this.domainType) && this.isValidDomain;
        }
        return visible;
      },
    },
    watch: {
      lang() {
        this.computedProductDetails();
        this.getPaymentPeriod(this.paymentPeriod);
      },
      currency() {
        this.computedProductDetails();
        this.getPaymentPeriod(this.paymentPeriod);
      },
      trialAuth: {
        handler(val) {
          if(val && this.isTrialFlow) { this.cartOrder() }
        },
        immediate: true,
        deep: true
      },
      summaryList() {
        setTimeout(() => {
          if(this.isValidDomain && !this.gtmTrackEvent) { this.gtmEvent() };
        }, 4000)
      },
      paymentPeriod(payment) {
        const sslPeriod = this.initialData?.period_for_free_ssl?.toLowerCase();
        if (!sslPeriod || !payment.period) {
          this.isFreeSslPeriod = null;
          return;
        }
        if (sslPeriod === 'any') {
          this.isFreeSslPeriod = true;
          return;
        }

        if(this.billingcycles) {
          const periods = this.billingcycles.map(item => item.period.toLowerCase());
          this.isFreeSslPeriod = periods.includes(payment.period.toLowerCase()) && periods.indexOf(payment.period.toLowerCase()) >= periods.indexOf(sslPeriod);
        }
      }
    }
  }
</script>

<style lang="scss">

  .order-form {
    margin-top: 40px;

    .container {
      padding-bottom: 50px;

      &:after {
        content:"";
        display:block;
        clear:both;
      }

      @media (max-width: $desktop) {
        display: flex;
        flex-wrap: wrap;
        margin-top: 30px;
        justify-content: center;
      }
    }

    .content {
      padding-right: 20px;
      width: calc(100% - 300px);

      @media (max-width: $desktop) {
        width: 100%;
        padding-right: 0;

        &.settings {
          order: 1;
        }

        &.completion {
          order: 3;
        }
      }
    }

    .sidebar {
      width: 300px;
      float: right;

      @media (max-width: $desktop) {
        width: calc(100% + #{$trim}*2);
        float: none;
        order: 2;
        height: auto !important;
        max-width: 429px;
        margin-right: -$trim;
        margin-left: -$trim;
        padding: 8px 0 29px;

        display: none;

        &.is-complete {
          display: block;
        }
      }

      .inner-wrapper-sticky {
        max-width: 300px;

        @media (max-width: $desktop) {
          position: relative !important;
          top: 0 !important;
          left: 0 !important;
          transform: none !important;
          width: auto !important;
          max-width: none;
          height: auto !important;
        }
      }
    }

    .block {
      margin-bottom: $row-padding;

      @media (max-width: $tablet) {
        margin-right: -$trim;
        margin-left: -$trim;
        margin-bottom: $row-padding;
        border-radius: 0;
      }
    }

    .breadcrumbs {
      padding-left: 0;
      padding-bottom: 42px;

      @media (max-width: $tablet) {
        padding-bottom: 30px;
      }
    }
  }

  .breadcrumbs {
    display: flex;
    flex-wrap: wrap;
    counter-reset: item;
    list-style-type: none;
    font-size: 19px;
    margin-bottom: -10px;

    @media (max-width: $tablet) {
      margin-bottom: 0;
    }

    &.steppers {

      li {

        &:not(:last-child) {

          &:after {
            @media (min-width: $tablet) {
              width: 28px;
            }
          }
        }

        &.hover:hover {
          cursor: pointer;

          &:before {
            color: $black;
            font-weight: 700;
          }
        }
      }
    }

    li {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      position: relative;
      padding-left: 34px;
      margin-bottom: 10px;
      transition: $transition;

      @media (max-width: $tablet) {
        width: 100%;
        justify-content: flex-start;
        margin-bottom: 0;
      }

      &:not(:last-child) {
        padding-right: 15px;

        @media (max-width: $tablet) {

          &:not(:last-child) {
            padding-bottom: 18px;
          }
        }
      }

      &:before {
        transition: $transition;
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        content: counter(item);
        counter-increment: item;
        width: 24px;
        height: 24px;
        border-radius: 25px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 16px;
        color: rgba($black, .3);
        background-color: rgba($black, .1);
      }

      &:first-child,
      &.active,
      &.complete {
        transition: text-shadow .3s;
        text-shadow: .5px 0px .1px, -.5px 0px .1px;

        &:before {
          font-weight: 700;
          color: $white;
          background-color: $primary;
        }
      }

      &:not(:last-child) {
        padding-right: 16px;

        &:after {
          margin-left: 19px;
          content: '';
          display: inline-flex;
          height: 1px;
          width: 112px;
          background-color: rgba($black, .3);

          @media (max-width: $tablet) {
            width: 1px;
            height: calc(100% - 29px);
            position: absolute;
            bottom: 2px;
            left: 11px;
            margin-left: 0;
          }
        }
      }
    }
  }

  .submit-block {
    text-align: center;
    max-width: 680px;
    width: 100%;
    margin: 0 auto 50px;
    color: $gray;

    .link {
      font-size: inherit;
    }
  }

  .cards {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    padding-left: 0;

    li {
      list-style: none;
      padding: 0 18px;
      height: 30px;
      display: flex;
      align-items: center;
    }

    img {
      display: block;
      max-width: 64px;
      max-height: 20px;
      width: 100%;
      filter: grayscale(100%);
    }
  }
</style>
